import React from 'react';
import { MMediaImage } from '../../molecules/MMediaImage';
import { AEyebrow } from '../../atoms/AEyebrow';
import { ASubheader } from '../../atoms/ASubheader';
import { AAccentLine } from '../../atoms/AAccentLine';
import { ABody } from '../../atoms/ABody';
import { MButton } from '../../molecules/MButton';
import { MTag } from '../../molecules/MTag';
import * as Styled from './OCardStandard.styles';
import type { OCardStandardProps } from './OCardStandard.types';
import { OCardStandardContentAlignment, OCardStandardPadding } from './OCardStandard.types';

export const OCardStandard: React.FC<OCardStandardProps> = ({
  componentId,
  contentAlignment = OCardStandardContentAlignment.Left,
  padding = OCardStandardPadding.ContentOnly,
  mMediaImage,
  aEyebrow,
  aSubheader,
  aAccentLine,
  aBody,
  mButton,
  mButton2,
  mTag,
  ctaLink,
  className,
}) => {
  const hasContent = aEyebrow || aSubheader || aAccentLine || aBody || mButton || mButton2 || mTag;
  const isClickable = !!ctaLink && !mButton && !mButton2;

  const handleCardClick = (e: React.MouseEvent) => {
    if (isClickable && ctaLink) {
      if (e.target === e.currentTarget || !(e.target as HTMLElement).closest('button, a')) {
        window.location.href = ctaLink;
      }
    }
  };

  return (
    <Styled.Wrapper
      data-component-id={componentId}
      className={className}
      contentAlignment={contentAlignment}
      padding={padding}
      isClickable={isClickable}
      onClick={handleCardClick}
      role={isClickable ? 'link' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {mMediaImage && (
        <Styled.MediaWrapper>
          <MMediaImage
            aImage={mMediaImage.aImage}
            mButtonIcon={mMediaImage.mButtonIcon}
            aScrimGradient={mMediaImage.aScrimGradient}
            mTag={mMediaImage.mTag}
          />
        </Styled.MediaWrapper>
      )}

      {hasContent && (
        <Styled.ContentWrapper contentAlignment={contentAlignment} padding={padding}>
          {aEyebrow && (
            <Styled.EyebrowWrapper>
              <AEyebrow eyebrowText={aEyebrow.eyebrowText} />
            </Styled.EyebrowWrapper>
          )}

          {aSubheader && (
            <Styled.SubheaderWrapper>
              <ASubheader text={aSubheader.text} size={aSubheader.size} element={aSubheader.element} />
            </Styled.SubheaderWrapper>
          )}

          {aAccentLine && (
            <Styled.AccentLineWrapper>
              <AAccentLine size={aAccentLine.size} alignment={aAccentLine.alignment} />
            </Styled.AccentLineWrapper>
          )}

          {aBody && (
            <Styled.BodyWrapper>
              <ABody bodyText={aBody.bodyText} size={aBody.size} color={aBody.color} />
            </Styled.BodyWrapper>
          )}

          {(mButton || mButton2) && (
            <Styled.ButtonsWrapper>
              {mButton && (
                <Styled.ButtonWrapper>
                  <MButton
                    buttonText={mButton.buttonText}
                    buttonType={mButton.buttonType}
                    size={mButton.size}
                    ctaLink={mButton.ctaLink}
                    openInANewTab={mButton.openInANewTab}
                    iconPlacement={mButton.iconPlacement}
                    aIcon={mButton.aIcon}
                  />
                </Styled.ButtonWrapper>
              )}

              {mButton2 && (
                <Styled.ButtonWrapper>
                  <MButton
                    buttonText={mButton2.buttonText}
                    buttonType={mButton2.buttonType}
                    size={mButton2.size}
                    ctaLink={mButton2.ctaLink}
                    openInANewTab={mButton2.openInANewTab}
                    iconPlacement={mButton2.iconPlacement}
                    aIcon={mButton2.aIcon}
                  />
                </Styled.ButtonWrapper>
              )}
            </Styled.ButtonsWrapper>
          )}

          {mTag && (
            <Styled.TagWrapper>
              <MTag type={mTag.type} aLabel={mTag.aLabel} aIcon={mTag.aIcon} />
            </Styled.TagWrapper>
          )}
        </Styled.ContentWrapper>
      )}
    </Styled.Wrapper>
  );
};

export * from './OCardStandard.types';
